// Step 4: Tour descriptions - headline, detail text, card text, availability
// Required for published tours; optional for concept tours

import React from 'react';
import { Info } from 'lucide-react';
import { Tour } from '../../../types/tours';

interface StepDescriptionsProps {
  formData: Partial<Tour>;
  isConcept: boolean;
  onChange: (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => void;
}

const StepDescriptions: React.FC<StepDescriptionsProps> = ({ formData, isConcept, onChange }) => {
  const req = !isConcept;

  return (
    <div className="bg-[#1F2B1F] rounded-xl p-6 border border-white/10">
      <h3 className="text-lg font-semibold text-white mb-6">Tour Descriptions</h3>

      <div className="space-y-6">
        <div>
          <label htmlFor="tour_detail_headline" className="block text-sm font-medium text-white/70 mb-2">
            Headline {req && <span className="text-red-400">*</span>}
          </label>
          <input
            type="text"
            id="tour_detail_headline"
            name="tour_detail_headline"
            value={formData.tour_detail_headline || ''}
            onChange={onChange}
            placeholder="A compelling headline for this tour"
            className="w-full px-4 py-3 bg-white/5 border border-white/15 rounded-lg focus:outline-none focus:ring-2 focus:ring-[#E85C0D] text-white placeholder-white/30 transition-all"
          />
        </div>

        <div>
          <label htmlFor="tour_detail_description" className="block text-sm font-medium text-white/70 mb-2">
            Detailed Description {req && <span className="text-red-400">*</span>}
          </label>
          <textarea
            id="tour_detail_description"
            name="tour_detail_description"
            value={formData.tour_detail_description || ''}
            onChange={onChange}
            rows={6}
            placeholder="Describe the tour experience in detail..."
            className="w-full px-4 py-3 bg-white/5 border border-white/15 rounded-lg focus:outline-none focus:ring-2 focus:ring-[#E85C0D] text-white resize-none placeholder-white/30 transition-all"
          />
        </div>

        <div>
          <label htmlFor="tour_card_description" className="block text-sm font-medium text-white/70 mb-2">
            Card Description {req && <span className="text-red-400">*</span>}
            <span className="text-white/40 ml-2">(max 100 characters)</span>
          </label>
          <textarea
            id="tour_card_description"
            name="tour_card_description"
            value={formData.tour_card_description || ''}
            onChange={onChange}
            maxLength={100}
            rows={2}
            placeholder="Short description shown on tour cards..."
            className="w-full px-4 py-3 bg-white/5 border border-white/15 rounded-lg focus:outline-none focus:ring-2 focus:ring-[#E85C0D] text-white resize-none placeholder-white/30 transition-all"
          />
          <p className="text-xs text-white/40 mt-1">
            {formData.tour_card_description?.length || 0}/100 characters
          </p>
        </div>

        <div>
          <label htmlFor="availability" className="block text-sm font-medium text-white/70 mb-2">
            Availability {req && <span className="text-red-400">*</span>}
          </label>
          <div className="relative">
            <Info className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-white/30" />
            <input
              type="text"
              id="availability"
              name="availability"
              value={formData.availability || ''}
              onChange={onChange}
              placeholder="e.g., Available daily, booking required"
              className="w-full pl-12 pr-4 py-3 bg-white/5 border border-white/15 rounded-lg focus:outline-none focus:ring-2 focus:ring-[#E85C0D] text-white placeholder-white/30 transition-all"
            />
          </div>
        </div>
      </div>
    </div>
  );
};

export default StepDescriptions;
